import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@shared/lib/utils"

/** Diameter of the badge circle (matches text-[10px] + comfortable padding) */
const BADGE_SIZE = 18

const tabBadgeVariants = cva(
  "ml-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-medium leading-none",
  {
    variants: {
      variant: {
        destructive: "bg-destructive text-destructive-foreground",
        soft: "bg-red-100 text-red-700",
        muted: "bg-muted text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "destructive",
    },
  }
)

export interface TabBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof tabBadgeVariants> {
  count: number
}

const TabBadge = React.forwardRef<HTMLSpanElement, TabBadgeProps>(
  ({ className, variant, count, style, ...props }, ref) => {
    if (count <= 0) return null

    const isCircle = count < 10

    return (
      <span
        ref={ref}
        className={cn(tabBadgeVariants({ variant }), className)}
        style={{
          height: BADGE_SIZE,
          ...(isCircle
            ? { width: BADGE_SIZE }
            : { minWidth: BADGE_SIZE, paddingInline: 4 }),
          ...style,
        }}
        {...props}
      >
        {count}
      </span>
    )
  }
)

TabBadge.displayName = "TabBadge"

export { TabBadge }
// eslint-disable-next-line react-refresh/only-export-components
export { tabBadgeVariants }
